import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Target, Users, TrendingUp, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { leadsAPI } from '@/lib/api';

const STATUS_ORDER = ['new','attempting_contact','contacted','quoted','appointment','closed_won','not_interested'];
const STATUS_LABEL = {
  new: 'New', attempting_contact: 'Attempting', contacted: 'Contacted', quoted: 'Quoted',
  appointment: 'Appointment', closed_won: 'Closed Won', not_interested: 'Not Interested',
};
const REASON_LABEL = {
  price: 'Price', timing: 'Timing', competition: 'Competition',
  not_qualified: 'Not qualified', no_response: 'No response', other: 'Other',
};

export default function LeadsReportsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    leadsAPI.reports().then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-content flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={24} /></div>;
  if (!data) return <div className="page-content">No data.</div>;

  return (
    <div className="page-content space-y-6" data-testid="leads-reports-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Leads Reports</h1>
          <p className="text-sm text-slate-500">Pipeline snapshot, agent performance and product mix.</p>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Total leads" value={data.totals.leads} icon={Users} tone="slate" />
        <Kpi label="Assigned" value={data.totals.assigned} tone="blue" />
        <Kpi label="Unassigned" value={data.totals.unassigned} tone="amber" />
        <Kpi label="Closed Won" value={data.totals.closed_won} icon={Target} tone="emerald" />
        <Kpi label="Products sold" value={data.totals.products_sold} tone="indigo" />
        <Kpi
          label="Products / won"
          value={data.totals.products_per_won_lead}
          tone={data.totals.products_per_won_lead >= 1.5 ? 'emerald' : 'rose'}
          icon={TrendingUp}
          hint={data.totals.products_per_won_lead >= 1.5 ? 'Target reached (≥1.5)' : 'Below target (1.5)'}
        />
      </div>

      {/* By status */}
      <Card>
        <CardHeader><CardTitle className="text-base">Leads by status</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {STATUS_ORDER.map((s) => (
              <div key={s} className="border rounded p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">{STATUS_LABEL[s]}</p>
                <p className="text-xl font-semibold">{data.by_status[s] || 0}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Agent performance */}
      <Card>
        <CardHeader><CardTitle className="text-base">Agent performance</CardTitle></CardHeader>
        <CardContent className="p-0">
          {data.by_agent.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No agents with assigned leads yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Agent</th>
                    <th className="text-right px-3 py-2 font-medium">Total</th>
                    <th className="text-right px-3 py-2 font-medium">Worked</th>
                    <th className="text-right px-3 py-2 font-medium">Not worked</th>
                    <th className="text-right px-3 py-2 font-medium">Closed won</th>
                    <th className="text-right px-3 py-2 font-medium">Products/lead</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_agent.map((a) => (
                    <tr key={a.agent_npn} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2 font-medium">{a.agent_name || a.agent_npn}</td>
                      <td className="px-3 py-2 text-right">{a.total}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{a.worked}</td>
                      <td className={`px-3 py-2 text-right ${a.not_worked > 0 ? 'text-rose-700 font-medium' : 'text-slate-400'}`}>{a.not_worked}</td>
                      <td className="px-3 py-2 text-right">{a.closed_won}</td>
                      <td className={`px-3 py-2 text-right font-medium ${a.products_per_lead >= 1.5 ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {a.products_per_lead.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agents with unworked */}
      {data.agents_with_unworked.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600" /> Agents with unworked leads</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.agents_with_unworked.map((a) => (
                <div key={a.agent_npn} className="bg-white border rounded p-2 text-sm">
                  <p className="font-medium">{a.agent_name || a.agent_npn}</p>
                  <p className="text-xs text-slate-500">{a.unworked} unworked / {a.total} total</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RankCard title="By category" rows={data.by_category} keyName="category" empty="No sales yet" />
        <RankCard title="By carrier" rows={data.by_carrier} keyName="carrier" empty="No sales yet" />
        <RankCard title="By product" rows={data.by_contract_type} keyName="contract_type" empty="No sales yet" />
      </div>

      {/* Not interested reasons */}
      <Card>
        <CardHeader><CardTitle className="text-base">Not Interested — reasons</CardTitle></CardHeader>
        <CardContent>
          {data.not_interested_reasons.length === 0 ? (
            <div className="text-slate-500 text-sm">No leads marked as not interested.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.not_interested_reasons.map((r) => (
                <div key={r.reason} className="border rounded p-2 flex items-center justify-between">
                  <span className="text-sm">{REASON_LABEL[r.reason] || r.reason}</span>
                  <Badge className="bg-rose-100 text-rose-700 border-rose-300 border font-normal">{r.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = 'slate', hint }) {
  const bg = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  }[tone] || 'bg-slate-50 border-slate-200 text-slate-900';
  return (
    <div className={`border rounded-md p-3 ${bg}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide opacity-80">{label}</span>
        {Icon && <Icon size={14} className="opacity-70" />}
      </div>
      <p className="text-2xl font-semibold mt-1">{value}</p>
      {hint && <p className="text-[10px] opacity-70 mt-0.5">{hint}</p>}
    </div>
  );
}

function RankCard({ title, rows, keyName, empty }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">{empty}</p>
        ) : (
          <div className="space-y-1.5">
            {rows.slice(0, 10).map((r) => (
              <div key={r[keyName]} className="flex items-center justify-between text-sm">
                <span className="truncate">{r[keyName] || 'Unknown'}</span>
                <Badge className="bg-slate-100 text-slate-700 border-slate-300 border font-normal">{r.count}</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
